import { useRef, useContext, useState, useEffect, useCallback, Ref, MutableRefObject } from 'react'
import type { TFinishSelectionCallback } from 'react-mouse-select'
import { ReactMouseSelect } from 'react-mouse-select'
import _ from 'lodash'
import { MediaLibraryActions } from '../../providers/MLProvider'
import { MLContext } from '../../providers'
import type {
  MediaLibrarySearchCollectionResponse,
  MediaLibrarySearchFileResponse,
  MediaLibrarySearchFolderResponse,
} from '../../interfaces/MediaLibrarySearchResponse'

interface Props {
  // onClickPreventDefault: If = true, the event bubbling is prevented after the selection
  // With a normal click (without selection) the event happens standardly
  // Useful, when there is a click handler on the container,
  // which shouldn’t happen in case of selection
  onClickPreventDefault?: boolean
  containerRef?: MutableRefObject<HTMLDivElement | null>
}

function MouseDragSelect({
  onClickPreventDefault = true,
  containerRef,
}: Props): React.ReactElement | null {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (containerRef?.current) {
      setPortalContainer(containerRef.current);
    }
  }, [containerRef]);

  const [{ folderList, fileList, mediaCollectionList }, dispatch] =
    useContext(MLContext)

  // Create two references to the folder and file lists,
  // and set their initial values to empty arrays
  const foldersRef = useRef<MediaLibrarySearchFolderResponse[]>([])
  const filesRef = useRef<MediaLibrarySearchFileResponse[]>([])
  const mediaCollectionRef = useRef<MediaLibrarySearchCollectionResponse[]>([])
  // Create a ref to keep track of whether the user is currently selecting items
  const isSelectingRef = useRef<boolean>()

  // Update the references to the folder and file lists,
  //  whenever folderList or fileList change refs are used instead of state
  //  because inside the finishSelection callback state references would be stale,
  //  (even when used with useCallback)
  foldersRef.current = folderList
  filesRef.current = fileList
  mediaCollectionRef.current = mediaCollectionList
  // Define a callback function to be called when the user starts selecting items
  const handleStartSelection = (): void => {
    // reset previous selection if any
    dispatch({
      type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
    })
    dispatch({
      type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
    })
    isSelectingRef.current = true
  }

  // Define a callback function to be called when the user finishes selecting items
  const finishSelection: TFinishSelectionCallback = useCallback(
    selectedItems => {
      // If the user is not currently selecting items, exit the function
      if (!isSelectingRef.current) {
        isSelectingRef.current = false
        return
      }
      const allSelectedIds = selectedItems.map(item =>
        item.getAttribute('data-id'),
      )

      // Separate the selected folder ids from the selected file ids
      const selectedFolderIds = allSelectedIds
        .filter(item => item?.includes('folder'))
        .map(str => str?.split('-')[1])

      const selectedFileIds = allSelectedIds
        .filter(item => item?.includes('file'))
        .map(str => str?.split('-')[1])

      const selectedCollectionIds = allSelectedIds
        .filter(item => item?.includes('mediaCollection'))
        .map(str => str?.split('-')[1])

      const filteredFolders = foldersRef.current.filter(item =>
        selectedFolderIds.includes(item.folderId),
      )

      const filteredFiles = filesRef.current.filter(item =>
        selectedFileIds.includes(item.fileId),
      )

      const filteredMediaCollections = mediaCollectionRef.current.filter(item =>
        selectedCollectionIds.includes(item.mediaCollectionId),
      )

      // Dispatch actions to update the selected folders and files in the state
      if (filteredFiles.length > 0) {
        dispatch({
          type: MediaLibraryActions.SET_SELECTED_FILES,
          payload: filteredFiles,
        })
      }
      if (filteredFolders.length > 0) {
        dispatch({
          type: MediaLibraryActions.SET_SELECTED_FOLDERS,
          payload: filteredFolders,
        })
      }
      if (filteredMediaCollections.length > 0) {
        dispatch({
          type: MediaLibraryActions.SET_SELECTED_MEDIA_COLLECTIONS,
          payload: filteredMediaCollections,
        })
      }

      isSelectingRef.current = false
      // fix safari issue where this class isn't removed by default
      _.delay(() => {
        const selectedClasses = document.getElementsByClassName('selected')
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = selectedClasses.length - 1; i >= 0; i--) {
          const item = selectedClasses[i]
          item.classList.remove('selected')
        }
      }, 0)
    },
    [],
  )

  if (!portalContainer) {
    return null;
  }
  return (
    <ReactMouseSelect
      containerRef={containerRef}
      portalContainer={portalContainer}
      itemClassName="mouse-select__selectable"
      sensitivity={0}
      tolerance={10}
      notStartWithSelectableElements={false}
      startSelectionCallback={handleStartSelection}
      finishSelectionCallback={finishSelection}
      onClickPreventDefault={onClickPreventDefault}
    />
  )
}

export default MouseDragSelect
